// The patient's waitlist requests, on the Appointments page.
//
// Client ticket 328. Until this existed the portal could put somebody on a list
// and then offer no way to see it, change it or leave — they could join from
// the booking site and were then stuck.
//
// ## It is a tab, chosen deliberately
//
// Jane stacks its "Wait List Requests" under the appointments on one screen,
// and the tabs beside this one are filters over appointments rather than over
// objects of different kinds. A tab was chosen anyway, because the thing a
// patient looks for is "where do I see my waitlist" and a tab is where they
// look. Discoverability beat taxonomy.
//
// What that costs, and is paid for here: the tab needs a COUNT, so the list is
// loaded by the page through `useMyWaitlistRequests` and handed down. The
// alternative — this component fetching for itself while the tab counts
// separately — is two answers to "how many am I waiting on" on the same screen.
//
// ## Nothing here recomputes a rule the engine owns
//
// The queue position after an edit comes from the server's
// `keptQueuePosition`, and the order of the list is the order the server sent
// (`createdAt` IS the queue position). A screen that sorted differently from
// the engine would teach the patient the wrong mental model about who is next.

import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {useMemo, useState} from "react";
import {FiClock} from "react-icons/fi";
import {leaveMyWaitlistRequest, replaceMyWaitlistRequest} from "./api";
import WaitlistRequestCard from "./WaitlistRequestCard";
import WaitlistRequestDialog from "./WaitlistRequestDialog";
import {forLocation} from "./locationScope";
import LeaveWaitlistDialog from "./LeaveWaitlistDialog";

export default function WaitlistRequests({
  dashState,
  entries,
  allowEdits,
  loading,
  failed,
  reload,
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [leaving, setLeaving] = useState(null);
  const editDialog = useDisclosure();
  const leaveDialog = useDisclosure();

  const serviceNames = useMemo(() => {
    const names = {};
    (dashState.services || []).forEach((service) => {
      names[String(service.serviceId)] =
        service.serviceName || String(service.serviceId);
    });
    return names;
  }, [dashState.services]);

  const clinicianNames = useMemo(() => {
    const names = {};
    (dashState.doctors || []).forEach((doctor) => {
      names[doctor.did] =
        `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim() ||
        doctor.name ||
        doctor.did;
    });
    return names;
  }, [dashState.doctors]);

  const cliniciansById = useMemo(() => {
    const byId = {};
    (dashState.doctors || []).forEach((doctor) => {
      byId[doctor.did] = doctor;
    });
    return byId;
  }, [dashState.doctors]);

  // What the dialog may OFFER, scoped to the location the entry names — not to
  // the patient's home site, which is the only one dashState was ever loaded
  // for. Offering the home site's catalogue is how a request at Coastal came to
  // name a clinician who works only at central.
  //
  // Deliberately not used for `serviceNames` / `clinicianNames` above. Those
  // stay unfiltered so a value the entry already holds can still be NAMED once
  // it is no longer on offer here: showing the truth and marking it unavailable
  // beats showing a convincing lie.
  const editingServices = useMemo(
    () => forLocation(dashState.services, editing?.locationId),
    [dashState.services, editing]
  );

  const editingClinicians = useMemo(
    () => forLocation(dashState.doctors, editing?.locationId),
    [dashState.doctors, editing]
  );

  const locationNames = useMemo(() => {
    const names = {};
    (dashState.locations || []).forEach((location) => {
      names[location.lid] = location.name || location.lid;
    });
    return names;
  }, [dashState.locations]);

  async function handleSave(draft) {
    setSaving(true);
    try {
      const result = await replaceMyWaitlistRequest(
        dashState,
        editing.entryId,
        draft
      );
      // Told, not inferred. The rule about what costs the queue position lives
      // in the engine, and reading the answer back is what keeps this screen
      // from having its own opinion.
      toast({
        title: "Your request was updated",
        description: result.keptQueuePosition
          ? "You kept your place in the queue."
          : "This is a new request, so it starts at the back of the queue.",
        status: "success",
        duration: 6000,
        isClosable: true,
      });
      editDialog.onClose();
      setEditing(null);
      await reload();
    } catch (error) {
      toast({
        title: "Could not update your request",
        description:
          error?.response?.data?.error ||
          "Please try again, or call the clinic.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleLeave() {
    setSaving(true);
    try {
      await leaveMyWaitlistRequest(dashState, leaving.entryId);
      toast({
        title: "You have been taken off the list",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      leaveDialog.onClose();
      setLeaving(null);
      await reload();
    } catch (error) {
      toast({
        title: "Could not take you off the list",
        description:
          error?.response?.data?.error ||
          "Please try again, or call the clinic.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }

  // NOTHING IS HIDDEN. As a stacked section this returned null when there was
  // nothing to show, which was right there and wrong here: somebody who clicked
  // a tab called Waitlist and got a blank panel learns nothing, and a failed
  // read looked exactly like an empty list.

  return (
    <Box>
      {loading ? (
        <Flex justify="center" py={8}>
          <Spinner size="sm" color="#6b7280" />
        </Flex>
      ) : failed ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={16}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
        >
          <Icon as={FiClock} fontSize="32px" color="gray.300" mb={3} />
          <Text fontSize="14px" color="#6b7280" fontWeight="500">
            Could not load your waitlist requests
          </Text>
          <Text fontSize="12px" color="gray.400" mt={1}>
            This does not mean you are on no list. Refresh, or call the clinic.
          </Text>
          <Button size="xs" variant="outline" mt={4} onClick={reload}>
            Try again
          </Button>
        </Flex>
      ) : entries.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          py={16}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="8px"
        >
          <Icon as={FiClock} fontSize="32px" color="gray.300" mb={3} />
          <Text fontSize="14px" color="#6b7280" fontWeight="500">
            No waitlist requests
          </Text>
          <Text fontSize="12px" color="gray.400" mt={1}>
            Ask to be added when you book, and it will show up here.
          </Text>
        </Flex>
      ) : (
        <Grid
          templateColumns={{base: "1fr", lg: "1fr 1fr"}}
          gap={3}
        >
          {entries.map((entry) => (
            <WaitlistRequestCard
              key={entry.entryId}
              entry={entry}
              serviceName={
                serviceNames[String(entry.serviceId)] || String(entry.serviceId)
              }
              clinicianName={
                entry.clinicianId
                  ? clinicianNames[entry.clinicianId] || entry.clinicianId
                  : "Anyone available"
              }
              clinician={cliniciansById[entry.clinicianId]}
              locationName={locationNames[entry.locationId]}
              editable={allowEdits[entry.locationId] !== false}
              onEdit={() => {
                setEditing(entry);
                editDialog.onOpen();
              }}
              onLeave={() => {
                setLeaving(entry);
                leaveDialog.onOpen();
              }}
            />
          ))}
        </Grid>
      )}

      <WaitlistRequestDialog
        isOpen={editDialog.isOpen}
        onClose={() => {
          editDialog.onClose();
          setEditing(null);
        }}
        entry={editing}
        services={editingServices}
        clinicians={editingClinicians}
        serviceName={editing ? serviceNames[String(editing.serviceId)] || "" : ""}
        clinicianName={
          editing?.clinicianId ? clinicianNames[editing.clinicianId] || "" : ""
        }
        onSave={handleSave}
        isSaving={saving}
      />

      <LeaveWaitlistDialog
        isOpen={leaveDialog.isOpen}
        onClose={() => {
          leaveDialog.onClose();
          setLeaving(null);
        }}
        entry={leaving}
        serviceName={
          leaving ? serviceNames[String(leaving.serviceId)] || "" : ""
        }
        onConfirm={handleLeave}
        isLoading={saving}
      />
    </Box>
  );
}
